import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Col, Row } from 'react-bootstrap';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import moment from 'moment';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

function AddServiceDialog({
  visible,
  startDate,
  endDate,
  driver,
  vehicle,
  assigned,
  route,
  onHide,
  onSave
}) {

  // #region vars and consts

  const [routeStatuses, setRouteStatuses] = useState([]);
  const [routeStatus, setRouteStatus] = useState(null);
  const [comments, setComments] = useState('');
  const [selectService, setSelectService] = useState(true);
  const [services, setServices] = useState([]);  
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    'global': { value: null, matchMode: FilterMatchMode.CONTAINS },        
    "externalOrderId": { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    "zone": { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    "purchaseDateT": { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    'departureTimeT': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] }
  });
  
  // #endregion

  // #region methods

  const getRouteStatuses = () => {    
    fetch(`${process.env.REACT_APP_API_URL}/airportService/v1/routeStatus/cmb`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setRouteStatuses(data.responseObject);          
        } else {
          console.warn('Error getting Route Statuses', data);
        }        
      });
  };

  const getServices = (
    startDate,
    endDate,
    vehicleId
  ) => {
    fetch(`${process.env.REACT_APP_API_URL}/airportService/v1/order/findOrderNotAssigned`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start: startDate,
        end: endDate,
        vehicleId
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setServices(data.responseObject);
        } else {
          console.warn('Error getting Services', data);
        }
      });
  };

  const formatDate = (millisecondsDate) => {
    if (millisecondsDate > 0) {
      const dateMoment = moment(millisecondsDate);
      return dateMoment.format("DD/MM/YYYY hh:mm:ss a");
    } else {
      return '';
    }
  };

  const addService = (
    routeId,
    orderEntityId,
  ) => {
    fetch(`${process.env.REACT_APP_API_URL}/airportService/v1/route/matchOrderRoute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        routeId,
        orderEntityId
      }),
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.log("addService: matchOrderRoute: success");
        onSave();        
      } else {
        console.log("addService: matchOrderRoute: error");
      }
    });
  };

  // #endregion

  // #region events

  useEffect(() => {    
    getRouteStatuses();    
    if (
      visible 
      && route 
      && vehicle
    ) {
      getServices(
        startDate.valueOf(),
        endDate.valueOf(),
        vehicle.vehicleId
      );
    }
  }, [visible, route, vehicle]);

  const onDialogHide = () => {
    if (onHide) onHide();
  };

  const onSelectServiceColumnClick = (service) => {
    addService(
      route.routeId,
      service.orderEntityId
    );
  };

  // #endregion

  // #region templates

  const summaryTemplate = (
    <>
      <Row>
        <Col md={6}>
          <span>
            Folio vuelta:&nbsp;
            <b>
              {route ? route.routeId : ''}
            </b>
          </span>
        </Col>
        <Col md={6}>
          <span>
            Vehículo:&nbsp;
            <b>
              {route ? route.vehicleNo : ''}
            </b>
          </span>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <span>
            Salida Aeropuerto:&nbsp;
            <b>
              {route ? formatDate(route.outAirportDateT) : ''}
            </b>
          </span>
        </Col>
        <Col md={6}>
          <span>
            Entrada Aeropuerto:&nbsp;
            <b>
              {route ? formatDate(route.inAirportDateT) : ''}
            </b>
          </span>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <span>
            Zona:&nbsp;
            <b>
              {route ? route.zoneRouteDesc : ''}
            </b>
          </span>
        </Col>
      </Row>
    </>
  );
  
  const selectServiceColumnTemplate = (service) => {
    return (
      <>
        <Button
          label="Seleccionar"
          className="p-button-link p-button-success"
          icon="pi pi-check"
          iconPos="left"
          onClick={() => onSelectServiceColumnClick(service)}
        />
      </>
    )
  };

  const purchaseDateTTemplate = (row) => {
    const purchaseDateT = row.purchaseDateT;
    if (purchaseDateT > 0) {
      const purchaseDateTMoment = moment(purchaseDateT);
      return (
        <span>{purchaseDateTMoment.format("DD/MM/YYYY hh:mm:ss a")}</span>
      );
    } else {
      return <></>;
    }
  };

  const departureTimeTTemplate = (row) => {
    const departureTimeT = row.departureTimeT;
    if (departureTimeT > 0) {
      const departureTimeTMoment = moment(departureTimeT);
      return (
        <span>{departureTimeTMoment.format("DD/MM/YYYY hh:mm:ss a")}</span>
      );
    } else {
      return <></>;
    }
  };

  const gridTemplate = (
    <>
      <div>
        <div className="card">
          <DataTable
            value={services}
            paginator
            className="p-datatable-customers"
            showGridlines
            stripedRows
            rows={50}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            rowsPerPageOptions={[10, 25, 50]}
            filters={filters}
            filterDisplay="menu"
            loading={loading}
            responsiveLayout="scroll"
            globalFilterFields={[
              'externalOrderId',
              'zone',
              'purchaseDateT',
              'departureTimeT'
            ]}
            emptyMessage="No records found."
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"                   
          >
            <Column body={selectServiceColumnTemplate} className='app-w-50-px text-center' />
            <Column field="externalOrderId" header="No. servicio" sortable filter filterPlaceholder="Buscar por No. servicio" className='app-w-250-px text-end' />
            <Column field="zone" header="Zona" sortable filter filterPlaceholder="Buscar por Zona" className='app-w-200-px text-start' />
            <Column field="purchaseDateT" header="Fecha de compra" sortable filter filterPlaceholder="Buscar por Fecha de compra" className='app-w-300-px text-start' body={purchaseDateTTemplate} />            
            <Column field="departureTimeT" header="Fecha de inicio" sortable filter filterPlaceholder="Buscar por Fecha de inicio" className='app-w-300-px text-start' body={departureTimeTTemplate} />            
          </DataTable>
        </div>
      </div>
    </>
  );

  // #endregion

  return (
    <>
      <article>
        <Dialog
          header="Agregar Servicio"
          visible={visible}
          onHide={onDialogHide}
          breakpoints={{ '960px': '75vw' }}
          style={{ width: '50vw' }}
        >
          {summaryTemplate}  
          {gridTemplate}
        </Dialog>
      </article>
    </>
  );
}

export default AddServiceDialog;